import React, { Fragment, FunctionComponent, ReactNode } from 'react';
import { InjectedIntl } from 'react-intl';
import { GenericProps } from 'types/interfaces';
import { Heading } from '@nbc-design/heading';
import Title from 'components/Title';
import { getTextFactory, getTextListFactory } from 'utils/TextUtils';
import { createErrorFormatter, isJointMenu } from 'utils/productFormUtils';
import { Participant, RegulatoryRadioItem } from './types';
import { Col, Row } from 'react-flexbox-grid';
import { ProductFormComboRadio, ProductFormRadioBoolean, ProductFormRadioSelect } from 'components/productFormFields';
import HelpText from 'components/HelpText';
import { NOTARIZEDBYPROXY, TRANSACTIONBYPROXY } from './constants';

import { Text } from '@nbc-design/text';
import { Link } from '@nbc-design/link';
import { URLS } from 'utils/constants';
import { ErrorFieldFilledColor } from '@nbc-design/icons/lib/web/ErrorFieldFilledColor';
import { useKycQuestions } from 'hooks/useKycQuestion/useKycQuestion';
import { KycQuestionCodes, KycQuestionKeys } from 'hooks/useKycQuestion/constants';

import './styles.scss';

export type RegulatoryProps = GenericProps & {
  intl: InjectedIntl;
  displayJointSurvivorship?: boolean;
  hasTraderOnAccount?: boolean;
  proxyType?: string;
  firstParticipant?: Participant;
  secondParticipant?: Participant;
};

const Regulatory: FunctionComponent<RegulatoryProps> = (props: RegulatoryProps) => {
  const {
    intl,
    firstParticipant,
    hasTraderOnAccount = false,
    secondParticipant,
    displayJointSurvivorship = false,
    proxyType,
    meta,
    change,
  } = props;

  React.useEffect(() => {
    if (firstParticipant) {
      change('firstParticipant.hasTrustedContactPerson', firstParticipant.hasTrustedContactPerson);
    }

    if (secondParticipant) {
      change('secondParticipant.hasTrustedContactPerson', secondParticipant.hasTrustedContactPerson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getText = getTextFactory(intl, 'regulatory');
  const getTextList = getTextListFactory(intl, 'regulatory');
  const getGlobalText = getTextFactory(intl, 'global');
  const formatErrorMessage = (prefix?: string, fieldName?: string) => createErrorFormatter(intl, prefix, fieldName);

  const {
    hasTraderOnAccount: kycTraderOnAccount,
    hasAnotherUserOnAccount: kycAnotherUserOnAccount,
    proxyType: kycProxyType,
  } = useKycQuestions({
    fieldMap: {
      hasTraderOnAccount: KycQuestionCodes.traderOnAccount,
      hasAnotherUserOnAccount: KycQuestionCodes.anotherUserOnAccount,
      proxyType: KycQuestionCodes.authorizedConductOperations,
    },
    productForm: props.productForm,
    locale: intl.locale,
    valuesKey: KycQuestionKeys.ACCOUNT_KYC_QUESTIONS,
    fallbackTitles: {
      hasTraderOnAccount: getText('hasTraderOnAccount'),
      hasAnotherUserOnAccount: getText('hasAnotherUserOnAccount'),
      proxyType: getText('proxyType'),
    },
  });

  const { hasTrustedContactPerson: kycTrustedContactPerson } = useKycQuestions({
    fieldMap: {
      hasTrustedContactPerson: KycQuestionCodes.trustedContactPersonIndicator,
    },
    productForm: props.productForm,
    locale: intl.locale,
    valuesKey: KycQuestionKeys.INVESTOR_KYC_QUESTIONS,
    fallbackTitles: {
      hasTrustedContactPerson: getText('hasTrustedContactPerson'),
    },
  });

  const getRadioBoolean = ({ name, condition, complement = null, helpText, label }: RegulatoryRadioItem): ReactNode => {
    if (!condition) return null;

    return (
      <Fragment key={name}>
        <Row>
          <Col xs={12} md={12} lg={12} data-test={`field_regulatory_question_${name}`}>
            <ProductFormRadioBoolean
              name={name}
              yesLabel={getGlobalText('button.yes')}
              noLabel={getGlobalText('button.no')}
              inline
              label={label ?? getText(name)}
              formatErrorMessage={formatErrorMessage()}
              className={`regulatory__question ${name}`}
              helpText={helpText}
            />
            {complement}
          </Col>
        </Row>
      </Fragment>
    );
  };

  const documentsTooltipText: ReactNode = (
    <div>
      <p className="section__explanation" data-test="text_explanation">
        <p>{getText('documentsTypeTooltip')}</p>
        <ol type="a">
          <li>{getText('documentsTypeTooltipA')}</li>
          <li>{getText('documentsTypeTooltipB')}</li>
          <li>{getText('documentsTypeTooltipC')}</li>
        </ol>
      </p>
    </div>
  );

  const documentsHelpText: ReactNode = (
    <div className="section__explanation" data-test="text_explanation">
      {getTextList('documentsTypeTooltipAgrements').map((text, i) => (
        <HelpText dataTest={`documentsTypeTooltipAgrements.${i + 1}`} key={`documentsTypeTooltipAgrements.${i + 1}`}>
          {text}
        </HelpText>
      ))}
    </div>
  );

  const getLegalText = (startText: string, linkText: string, endText: string): React.ReactNode => (
    <div className="regulatory-text-legal">
      <ErrorFieldFilledColor size="xsmall" title="error-field-filled-color" />
      <Text data-test="fields_proxy_Information" className="product-form-input__help legal">
        {getText(startText)}
        <Link
          href={URLS.FORMS[intl.locale.toUpperCase()]}
          target="_blank"
          rel="noopener noreferrer"
          underlined
          data-test="link_forms"
          className="forms-link"
        >
          {getText(linkText)}
        </Link>
        {getText(endText)}
      </Text>
    </div>
  );

  const proxyTypeComplementDescription =
    proxyType === TRANSACTIONBYPROXY
      ? getLegalText('proxyStartText', 'proxyFormsLink', 'proxyEndText')
      : proxyType === NOTARIZEDBYPROXY
      ? getLegalText('notaryProxyStartText', 'notaryProxyFormsLink', 'notaryProxyEndText')
      : '';

  const proxyTypeComplement: ReactNode | null = hasTraderOnAccount ? (
    <Row>
      <Col xs={12} md={12} lg={12}>
        <ProductFormRadioSelect
          className="no-bold"
          label={kycProxyType.title ?? getText('proxyType')}
          refData={kycProxyType.refData}
          name="proxyType"
          inline
          formatErrorMessage={formatErrorMessage()}
          helpText={proxyTypeComplementDescription}
        />
      </Col>
    </Row>
  ) : null;

  const trustedContactPersonComplementDescription = (accountHolder: Participant | undefined): string => {
    return accountHolder && accountHolder.hasTrustedContactPerson
      ? getText('trustedContactPersonComplementDescription')
      : '';
  };

  const isMultiHolderMode = isJointMenu(meta);

  const radioBooleanItems: RegulatoryRadioItem[] = [
    {
      name: 'hasTraderOnAccount',
      condition: true,
      complement: proxyTypeComplement,
      label: kycTraderOnAccount.title,
    },
    {
      name: 'hasAnotherUserOnAccount',
      condition: true,
      label: kycAnotherUserOnAccount.title,
    },
    {
      name: 'hasJointSurvivorship',
      condition: displayJointSurvivorship,
    },
    {
      name: 'firstParticipant.hasTrustedContactPerson',
      condition: true,
      label: kycTrustedContactPerson.title,
      helpText: trustedContactPersonComplementDescription(firstParticipant),
    },
    {
      name: 'secondParticipant.hasTrustedContactPerson',
      condition: true,
      label: kycTrustedContactPerson.title,
      helpText: trustedContactPersonComplementDescription(secondParticipant),
    },
  ];

  const hasCommunicationApproval = { name: 'hasCommunicationApproval', condition: true };

  return (
    <div className="regulatory__form">
      <Title label={getText('pageTitle')} />

      {isMultiHolderMode ? (
        <>
          {radioBooleanItems
            .filter(
              (accountHolderItem) =>
                accountHolderItem.name.search('firstParticipant') === -1 &&
                accountHolderItem.name.search('secondParticipant') === -1,
            )
            .map((item: RegulatoryRadioItem): ReactNode => getRadioBoolean(item))}
          <Heading type={'h3'} size={5}>
            {getText('applicantTitle')}
          </Heading>
          <hr />
          {radioBooleanItems
            .filter((accountHolderItem) => accountHolderItem.name.search('firstParticipant') !== -1)
            .map((item: RegulatoryRadioItem): ReactNode => getRadioBoolean(item))}
          <Heading type={'h3'} size={5}>
            {getText('spouseTitle')}
          </Heading>
          <hr />
          {radioBooleanItems
            .filter((accountHolderItem) => accountHolderItem.name.search('secondParticipant') !== -1)
            .map((item: RegulatoryRadioItem): ReactNode => getRadioBoolean(item))}
        </>
      ) : (
        <>
          {radioBooleanItems
            .filter((accountHolderItem) => accountHolderItem.name.search('secondParticipant') === -1)
            .map((item: RegulatoryRadioItem): ReactNode => getRadioBoolean(item))}
        </>
      )}

      <Heading type={'h2'} size={4}>
        {getText('beneficialShareholders')}
      </Heading>
      <hr />

      {getRadioBoolean(hasCommunicationApproval)}

      <Row>
        <Col xs={12} md={12} lg={12} data-test="field_documentsType">
          <ProductFormComboRadio
            name="documentsType"
            label={getText('documentsType')}
            formatErrorMessage={formatErrorMessage()}
            tooltip={documentsTooltipText}
            className="regulatory__documents"
          />
        </Col>
        {documentsHelpText}
      </Row>
    </div>
  );
};

export default Regulatory;
